import React, { useEffect, useRef } from 'react';

/**
 * Minimal Leaflet wrapper that lets the user drop / drag a pin to capture lat/lng.
 * No paid APIs — uses free OpenStreetMap tiles.
 *
 * Props:
 *   value:    { lat, lng } | null
 *   onChange: ({ lat, lng }) => void
 *   center:   optional [lat, lng] fallback when no value
 *   height:   px (default 260)
 */
export default function LocationPicker({ value, onChange, center, height = 260 }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;

    const load = async () => {
      // Inject Leaflet CSS once (the npm package's CSS isn't auto-imported)
      if (!document.querySelector('link[data-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.setAttribute('data-leaflet', '1');
        document.head.appendChild(link);
      }
      const L = (await import('leaflet')).default;
      // Fix default icon paths (Leaflet bundles icons that webpack doesn't resolve correctly)
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
      if (cancelled || !containerRef.current) return;

      const initial = value || (center ? { lat: center[0], lng: center[1] } : { lat: -9.4438, lng: 147.1803 });
      const map = L.map(containerRef.current, { zoomControl: true }).setView([initial.lat, initial.lng], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      }).addTo(map);
      const marker = L.marker([initial.lat, initial.lng], { draggable: true }).addTo(map);
      marker.on('dragend', () => {
        const ll = marker.getLatLng();
        onChangeRef.current?.({ lat: ll.lat, lng: ll.lng });
      });
      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        onChangeRef.current?.({ lat: e.latlng.lat, lng: e.latlng.lng });
      });
      mapRef.current = map;
      markerRef.current = marker;
      // If component mounted before container had final size, fix layout
      setTimeout(() => map.invalidateSize(), 80);
    };

    load();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes to marker (e.g. when user clicks "Use my GPS")
  useEffect(() => {
    if (mapRef.current && markerRef.current && value && typeof value.lat === 'number' && typeof value.lng === 'number') {
      markerRef.current.setLatLng([value.lat, value.lng]);
      mapRef.current.setView([value.lat, value.lng], Math.max(mapRef.current.getZoom(), 14));
    }
  }, [value?.lat, value?.lng]);

  return (
    <div
      ref={containerRef}
      data-testid="location-map"
      style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-gray)' }}
    />
  );
}
